import IdentifyApi from '@apis/auth/IdentifyApi';
import queries from '@apis/queries';
import UserApi from '@apis/user/UserApi';
import {
  ACCESS_TOKEN_MAX_AGE_SECOND,
  REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import useTermCategories from '@hooks/term/useTermCategories';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { SignInResponse } from '@models/auth';
import { ErrorResponse } from '@models/error';
import { useFlow } from '@router/stackflow';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';

import SignUpContext from '../../../context/SignUpContext';

function useSignUp() {
  const { pop } = useFlow();
  const {
    useVerified, useMarketing, usePhoneNo, kakaoId, appleId, nickname, useLocation,
    useSelectedTermCategories,
  } = useContext(SignUpContext);
  const project = useProject();
  const [verified] = useVerified;
  const [selectedTermCategories, setSelectedTermCategories] = useSelectedTermCategories;
  const termCategories = useTermCategories('SIGN_UP');
  const signUpEnabled = useMemo(() => {
    if (!verified && !project?.features.authenticationEnabled) {
      return false;
    }
    const requiredCategories = termCategories
      .filter(((tc) => tc.isRequired));
    return requiredCategories
      .every((rc) => selectedTermCategories.includes(rc.id));
  }, [verified, termCategories, selectedTermCategories, project]);
  const [marketingAgreed] = useMarketing;
  const [locationAgreed] = useLocation;
  const [phoneNo] = usePhoneNo;
  const projectId = useProjectId();
  const toast = useToast();
  const { startLoading, stopLoading } = useLoading();
  const [_, setCookie] = useCookies(['accessToken', 'refreshToken']);
  const queryClient = useQueryClient();
  const onSuccess = (data: SignInResponse) => {
    setCookie('accessToken', data.token, {
      path: '/',
      maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
    });
    setCookie('refreshToken', data.refreshToken, {
      path: '/',
      maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
    });
    queryClient.invalidateQueries({ queryKey: queries.user._def });
    pop(3);
  };
  const { mutate } = useMutation({
    mutationKey: ['signUp', projectId, phoneNo, marketingAgreed, appleId, kakaoId, nickname],
    mutationFn: () => UserApi.signUp({
      projectId, phoneNo, marketingAgreed, appleId, kakaoId, nickname, locationAgreed,
    }),
    onMutate() {
      startLoading();
    },
    onSuccess,
    onError(e: AxiosError<ErrorResponse>) {
      switch (e.response?.data.code) {
        case 'DUPLICATE_USER':
          toast.error('이미 가입된 사용자입니다.');
          break;
        default:
          toast.error('알 수 없는 오류가 발생했습니다.');
      }
      console.error(e);
    },
    onSettled() {
      stopLoading();
    },
  });

  const signUp = () => {
    if (project?.features.authenticationEnabled) {
      IdentifyApi.identify({
        projectId, marketingAgreed, appleId, kakaoId, nickname, locationAgreed,
      }).then((res) => {
        const form = document.createElement('form');
        form.hidden = true;
        form.action = 'https://nice.checkplus.co.kr/CheckPlusSafeModel/checkplus.cb';
        const mInput = document.createElement('input');
        mInput.name = 'm';
        mInput.value = 'checkplusService';
        const dataInput = document.createElement('input');
        dataInput.name = 'EncodeData';
        dataInput.value = res.encryptedData;
        form.append(mInput);
        form.append(dataInput);
        document.body.appendChild(form);
        form.submit();
      }).catch((e) => {
        console.error('본인인증 오류', e);
        toast.error('본인인증에 실패했습니다. 다시 시도해주세요.');
      });
    } else {
      mutate();
    }
  };

  return {
    signUpEnabled,
    signUp,
  };
}

export default useSignUp;
